// Page-level SEO helper. Sets the document title plus the description,
// keywords, canonical and Open Graph / Twitter tags for a route, and returns a
// cleanup that restores the previous values when the page unmounts. Centralising
// this keeps every section's TDK consistent and in sync.

import { siteUrl } from './structuredData';

interface PageSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
}

function upsertNameMeta(name: string, content: string): () => void {
  let el = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  const created = !el;
  const prev = el?.getAttribute('content') ?? '';
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute('content', prev);
  };
}

function upsertPropertyMeta(property: string, content: string): () => void {
  let el = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  const created = !el;
  const prev = el?.getAttribute('content') ?? '';
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute('content', prev);
  };
}

function upsertCanonical(href: string): () => void {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const created = !el;
  const prev = el?.getAttribute('href') ?? '';
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute('href', prev);
  };
}

export function applyPageSeo({ title, description, keywords, path }: PageSeo): () => void {
  const prevTitle = document.title;
  document.title = title;

  const canonicalUrl = siteUrl(path);

  const restores: Array<() => void> = [
    upsertNameMeta('description', description),
    upsertNameMeta('keywords', keywords),
    upsertPropertyMeta('og:title', title),
    upsertPropertyMeta('og:description', description),
    upsertPropertyMeta('og:url', canonicalUrl),
    upsertNameMeta('twitter:title', title),
    upsertNameMeta('twitter:description', description),
    upsertCanonical(canonicalUrl),
  ];

  return () => {
    document.title = prevTitle;
    restores.forEach((restore) => restore());
  };
}

// Marks a route as non-indexable (used by the 404 page).
export function applyNoIndex(): () => void {
  return upsertNameMeta('robots', 'noindex, nofollow');
}